import { Loader2, Plus, Shield, Users } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { toast } from 'react-toastify'
import { useOrganization } from '@/api/queries/organization-queries'
import { useCreateVirtualPerson } from '@/api/queries/people-queries'
import {
	useDeleteUser,
	useResetUserPassword,
	useUpdateUserRole,
	useUpdateUserStatus,
	useUsers,
} from '@/api/queries/users-queries'
import type { CreateVirtualUserFormData } from '@/components/administration'
import { CreateVirtualUserFormContent } from '@/components/administration'
import {
	AdminCardPage,
	ResponsiveFormModal,
	SearchInput,
} from '@/components/commons'
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { NativeSelect, NativeSelectOption } from '@/components/ui/native-select'
import { adminBackRoute } from '@/constants/admin-menu'
import { ROLE_LABELS } from '@/constants/role-labels'
import { ROUTES } from '@/constants/routes'
import { useDebouncedValue } from '@/hooks/use-debounced-value'
import type { PersonListItem } from '@/types'
import type { Role, UserOutput } from '@/types/auth'
import { ROLES } from '@/types/auth'
import { getApiErrorMessage } from '@/utils/api-error'

const USERS_PAGE_SIZE = 100

export const UsersPage = () => {
	const navigate = useNavigate()
	const { t } = useTranslation()

	const [page, setPage] = useState(1)
	const [search, setSearch] = useState('')
	const [roleFilter, setRoleFilter] = useState<Role | ''>('')
	const [statusFilter, setStatusFilter] = useState<string>('')
	const [resetTarget, setResetTarget] = useState<UserOutput | undefined>()
	const [deleteTarget, setDeleteTarget] = useState<UserOutput | undefined>()
	const [createOpen, setCreateOpen] = useState(false)
	const [createdPerson, setCreatedPerson] = useState<PersonListItem | null>(
		null,
	)
	const { debouncedValue: debouncedSearch } = useDebouncedValue(search, 500)

	const handleSearchChange = (value: string) => {
		setPage(1)
		setSearch(value)
	}

	const handleRoleFilterChange = (value: string) => {
		setPage(1)
		setRoleFilter(value as Role | '')
	}

	const handleStatusFilterChange = (value: string) => {
		setPage(1)
		setStatusFilter(value)
	}

	const params = {
		...(debouncedSearch.trim() ? { q: debouncedSearch.trim() } : {}),
		page,
		limit: USERS_PAGE_SIZE,
		...(roleFilter ? { role: roleFilter as Role } : {}),
		...(statusFilter ? { isActive: statusFilter as 'true' | 'false' } : {}),
	}

	const { data, isLoading } = useUsers(params)
	const updateRoleMutation = useUpdateUserRole()
	const updateStatusMutation = useUpdateUserStatus()
	const resetPasswordMutation = useResetUserPassword()
	const deleteUserMutation = useDeleteUser()
	const { data: organization } = useOrganization()
	const createVirtualPersonMutation = useCreateVirtualPerson()

	const users = data?.items ?? []
	const total = data?.total ?? 0
	const totalPages = data?.totalPages ?? 1
	const branches = organization ?? []

	const handleRoleChange = async (user: UserOutput, newRole: Role) => {
		try {
			await updateRoleMutation.mutateAsync({ id: user.id, role: newRole })
			toast.success(
				t('administration.users.roleChangeSuccess', {
					name: user.name,
					role: ROLE_LABELS[newRole],
				}),
			)
		} catch (_err) {
			toast.error(t('administration.users.roleChangeError'))
		}
	}

	const handleStatusToggle = async (user: UserOutput) => {
		try {
			await updateStatusMutation.mutateAsync({
				id: user.id,
				isActive: !user.isActive,
			})
			toast.success(
				user.isActive
					? t('administration.users.deactivateSuccess', { name: user.name })
					: t('administration.users.activateSuccess', { name: user.name }),
			)
		} catch (_err) {
			toast.error(t('administration.users.statusChangeError'))
		}
	}

	const handleResetPasswordConfirm = async () => {
		if (!resetTarget) return

		try {
			await resetPasswordMutation.mutateAsync(resetTarget.id)
			toast.success(
				t('administration.users.resetPasswordSuccess', {
					name: resetTarget.name,
				}),
			)
		} catch (_err) {
			toast.error(t('administration.users.resetPasswordError'))
		} finally {
			setResetTarget(undefined)
		}
	}

	const handleDeleteUserConfirm = async () => {
		if (!deleteTarget) return

		try {
			await deleteUserMutation.mutateAsync(deleteTarget.id)
			toast.success(
				t('administration.users.deleteSuccess', { name: deleteTarget.name }),
			)
		} catch (error) {
			toast.error(getApiErrorMessage(error))
		} finally {
			setDeleteTarget(undefined)
		}
	}

	const handleCreateVirtualUser = async (data: CreateVirtualUserFormData) => {
		try {
			const result = await createVirtualPersonMutation.mutateAsync(data)
			setCreatedPerson(result)
		} catch (error) {
			toast.error(getApiErrorMessage(error))
		}
	}

	const handleCreateModalOpenChange = (open: boolean) => {
		setCreateOpen(open)
		if (!open) setCreatedPerson(null)
	}

	const handleBack = () => {
		navigate(adminBackRoute(ROUTES.ADMINISTRATION.USERS))
	}

	return (
		<>
			<AdminCardPage
				icon={<Users className="size-5 text-white" />}
				title={t('administration.users.title')}
				subtitle={t('administration.users.subtitle')}
				onBack={handleBack}
				actions={
					<Button
						onClick={() => setCreateOpen(true)}
						className="gap-2 bg-kaefer-medium-grey text-white hover:bg-kaefer-medium-grey/90 w-full sm:w-auto"
					>
						<Plus className="size-4" />
						<span>{t('administration.users.form.createTrigger')}</span>
					</Button>
				}
				modal={
					<ResponsiveFormModal
						open={createOpen}
						onOpenChange={handleCreateModalOpenChange}
						title={t('administration.users.form.title')}
						dialogClassName="sm:max-w-md"
					>
						<CreateVirtualUserFormContent
							branches={branches}
							isLoading={createVirtualPersonMutation.isPending}
							successData={createdPerson}
							onSave={handleCreateVirtualUser}
							onClose={() => handleCreateModalOpenChange(false)}
						/>
					</ResponsiveFormModal>
				}
				footerText={`${total} ${total === 1 ? t('administration.users.countSingular') : t('administration.users.countPlural')}`}
			>
				{/* Filters */}
				<div className="flex flex-col gap-3 sm:flex-row sm:items-center">
					<SearchInput
						value={search}
						onChange={handleSearchChange}
						placeholder={t('administration.users.searchPlaceholder')}
						className="flex-1"
					/>
					<div className="flex flex-col sm:flex-row items-center gap-2 sm:w-3/5 lg:w-2/5">
						<NativeSelect
							value={roleFilter}
							onChange={(e) => handleRoleFilterChange(e.target.value)}
						>
							<NativeSelectOption value="">
								{t('administration.users.allRoles')}
							</NativeSelectOption>
							{ROLES.map((role) => (
								<NativeSelectOption key={role} value={role}>
									{ROLE_LABELS[role]}
								</NativeSelectOption>
							))}
						</NativeSelect>
						<NativeSelect
							value={statusFilter}
							onChange={(e) => handleStatusFilterChange(e.target.value)}
						>
							<NativeSelectOption value="">
								{t('administration.users.allStatus')}
							</NativeSelectOption>
							<NativeSelectOption value="true">
								{t('administration.users.active')}
							</NativeSelectOption>
							<NativeSelectOption value="false">
								{t('administration.users.inactive')}
							</NativeSelectOption>
						</NativeSelect>
					</div>
				</div>

				{/* List */}
				{isLoading ? (
					<div className="flex items-center justify-center py-12">
						<Loader2 className="h-6 w-6 animate-spin text-kaefer-medium-grey" />
					</div>
				) : users.length === 0 ? (
					<p className="py-8 text-center text-muted-foreground text-sm">
						{t('administration.users.noResults')}
					</p>
				) : (
					<div className="divide-y divide-kaefer-medium-grey/10 rounded-lg border border-kaefer-medium-grey/20">
						{users.map((user) => (
							<div
								key={user.id}
								className="flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
							>
								{/* User info */}
								<div className="min-w-0 flex-1">
									<div className="flex items-center gap-2">
										<span className="font-medium text-kaefer-dark-grey text-sm truncate">
											{user.name}
										</span>
										<span
											className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
												user.isActive
													? 'bg-green-100 text-green-700'
													: 'bg-red-100 text-red-700'
											}`}
										>
											{user.isActive
												? t('administration.users.active')
												: t('administration.users.inactive')}
										</span>
									</div>
									<div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-kaefer-medium-grey text-xs">
										<span>
											{t('administration.users.badge')}: {user.badge}
										</span>
										{user.crName && <span>CR: {user.crName}</span>}
									</div>
								</div>

								{/* Role selector + status toggle */}
								<div className="flex items-center gap-2">
									<div className="flex items-center gap-1.5">
										<Shield className="h-4 w-4 text-kaefer-medium-grey" />
										<NativeSelect
											value={user.role}
											onChange={(e) =>
												handleRoleChange(user, e.target.value as Role)
											}
											size="sm"
											className="w-36"
											disabled={
												updateRoleMutation.isPending ||
												updateStatusMutation.isPending ||
												resetPasswordMutation.isPending
											}
										>
											{ROLES.map((role) => (
												<NativeSelectOption key={role} value={role}>
													{ROLE_LABELS[role]}
												</NativeSelectOption>
											))}
										</NativeSelect>
									</div>
									<button
										type="button"
										onClick={() => setResetTarget(user)}
										disabled={
											resetPasswordMutation.isPending ||
											updateStatusMutation.isPending ||
											updateRoleMutation.isPending
										}
										className="inline-flex items-center gap-1.5 rounded-md bg-amber-50 px-3 py-1.5 font-medium text-amber-700 text-xs transition-colors hover:bg-amber-100 disabled:opacity-50"
									>
										{resetPasswordMutation.isPending ? (
											<Loader2 className="h-3.5 w-3.5 animate-spin" />
										) : null}
										{t('administration.users.resetPassword')}
									</button>

									<button
										type="button"
										onClick={() => handleStatusToggle(user)}
										disabled={
											updateStatusMutation.isPending ||
											updateRoleMutation.isPending ||
											resetPasswordMutation.isPending ||
											deleteUserMutation.isPending
										}
										className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
											user.isActive
												? 'bg-red-50 text-red-600 hover:bg-red-100'
												: 'bg-green-50 text-green-600 hover:bg-green-100'
										} disabled:opacity-50`}
									>
										{user.isActive
											? t('administration.users.deactivate')
											: t('administration.users.activate')}
									</button>
								</div>
							</div>
						))}
					</div>
				)}

				{/* Pagination */}
				{totalPages > 1 && (
					<div className="flex items-center justify-center gap-2 pt-2">
						<button
							type="button"
							disabled={page <= 1}
							onClick={() => setPage((p) => p - 1)}
							className="rounded-md border px-3 py-1.5 text-xs font-medium transition-colors hover:bg-kaefer-light-silver disabled:opacity-40"
						>
							{t('administration.users.previous')}
						</button>
						<span className="text-kaefer-medium-grey text-xs">
							{page} / {totalPages}
						</span>
						<button
							type="button"
							disabled={page >= totalPages}
							onClick={() => setPage((p) => p + 1)}
							className="rounded-md border px-3 py-1.5 text-xs font-medium transition-colors hover:bg-kaefer-light-silver disabled:opacity-40"
						>
							{t('administration.users.next')}
						</button>
					</div>
				)}
			</AdminCardPage>

			<AlertDialog
				open={!!resetTarget}
				onOpenChange={(open) => !open && setResetTarget(undefined)}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>
							{t('administration.users.resetPasswordTitle')}
						</AlertDialogTitle>
						<AlertDialogDescription>
							{t('administration.users.resetPasswordDescription', {
								name: resetTarget?.name ?? '',
							})}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>
							{t('administration.users.resetPasswordCancelButton')}
						</AlertDialogCancel>
						<AlertDialogAction
							onClick={handleResetPasswordConfirm}
							disabled={resetPasswordMutation.isPending}
							className="bg-amber-600 text-white hover:bg-amber-700"
						>
							{t('administration.users.resetPasswordConfirmButton')}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>

			<AlertDialog
				open={!!deleteTarget}
				onOpenChange={(open) => !open && setDeleteTarget(undefined)}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>
							{t('administration.users.deleteTitle')}
						</AlertDialogTitle>
						<AlertDialogDescription>
							{t('administration.users.deleteDescription', {
								name: deleteTarget?.name ?? '',
							})}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>
							{t('administration.users.deleteCancelButton')}
						</AlertDialogCancel>
						<AlertDialogAction
							onClick={handleDeleteUserConfirm}
							disabled={deleteUserMutation.isPending}
							className="bg-red-600 text-white hover:bg-red-700"
						>
							{t('administration.users.deleteConfirmButton')}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</>
	)
}
